import {
  PITCH_EVENT_NAMES,
  createMockPitchTrackerModule,
  effectiveEventIntervalMs,
} from '@muzly/shared';
import type {
  DiagnosticsSnapshot,
  PitchTrackerConfig,
  PitchTrackerEvent,
  PitchTrackerEventName,
  PitchTrackerEventOf,
  PitchTrackerModule,
  PitchTrackerSessionInfo,
  Subscription,
} from '@muzly/shared';

import { buildMicFrame } from './micFrames';

/**
 * browserSource.ts — 브라우저에서 세션이 무엇을 소스로 쓸지 고르는 `PitchTrackerModule`.
 *
 * **왜 모듈로 위장하나**: 콘솔의 모든 패널은 `native.on('pitchTrackerEvent')` 한 곳만
 * 구독한다. 그 채널로 프레임을 흘려보내는 배선(`usePitchSession` → `createPracticeBridge`
 * → `forwardToNativeChannel`)이 이미 있으므로, 소스를 모듈 인터페이스로 만들면 **패널은
 * 한 줄도 안 바뀐다.**
 *
 * `usePitchSession`은 transport를 mount-once로 잡고 모듈을 한 번만 만든다. 그래서 소스
 * 전환은 여기 `start()` 안에서 결정한다 — 세션을 다시 시작하면 새 소스로 열린다.
 *
 * **mock의 한계가 이 파일의 존재 이유다**: mock 프레임은 confidence 0.95 · rms −18dBFS ·
 * 고정 음높이라 어떤 임계값에도 걸리지 않는다 → 슬라이더를 굴려도 화면이 그대로다.
 * 실제 목소리가 들어와야 튜닝이 눈에 보인다. 대신 추정기는 JS ACF이고 **네이티브 MPM이
 * 아니다**(micFrames.ts 참고) — 프레임이 `algorithm: 'acf'`로 자기를 밝힌다.
 */

export type BrowserSource = 'mock' | 'mic';

export type MicFailure = 'denied' | 'unsupported';

/**
 * 브라우저 마이크 세션의 식별자. 소비처가 **어느 모듈이 실제로 열렸는지**를 세션에서
 * 읽어 출처를 정한다(`useVoiceCapture`) — mock의 `MOCK_SESSION_ID`와 같은 역할이다.
 */
export const BROWSER_MIC_SESSION_ID = 'browser-mic';

interface MicAudio {
  ctx: AudioContext;
  analyser: AnalyserNode;
  stream: MediaStream;
  /** 마이크 노드. 프레임 분석과 **같은 그래프**에 PCM 녹음을 물리려고 들고 있는다. */
  source: MediaStreamAudioSourceNode;
  // `getFloatTimeDomainData`는 ArrayBuffer 뒷받침을 요구한다(SharedArrayBuffer 불가).
  buffer: Float32Array<ArrayBuffer>;
}

/**
 * 마이크가 열렸을 때 바깥이 같은 오디오 그래프에 붙을 수 있게 넘기는 손잡이.
 *
 * 마이크를 두 번 열지 않으려고 있다: `getUserMedia`를 따로 부르면 스트림이 둘이 되고,
 * 기기에 따라 두 번째가 거부되거나 첫 번째와 다른 샘플레이트로 열린다. 그러면 f0
 * 프레임과 녹음 PCM이 서로 다른 시계를 쓰게 된다.
 */
export interface MicGraph {
  ctx: AudioContext;
  source: MediaStreamAudioSourceNode;
}

type Listeners = Map<
  PitchTrackerEventName,
  Set<(e: PitchTrackerEvent) => void>
>;

const emit = (listeners: Listeners, event: PitchTrackerEvent): void => {
  const set = listeners.get(event.type);
  if (!set) {
    return;
  }
  for (const listener of set) {
    listener(event);
  }
};

const resolveAudioContextCtor = (): typeof AudioContext | undefined =>
  window.AudioContext ??
  (window as unknown as { webkitAudioContext?: typeof AudioContext })
    .webkitAudioContext;

/** 요청 레이트로 열어 보고, 기기가 거부하면 기본 레이트로 되돌아간다. */
const openContext = (
  Ctor: typeof AudioContext,
  sampleRate: number,
): AudioContext => {
  try {
    return new Ctor({ sampleRate });
  } catch {
    return new Ctor();
  }
};

/**
 * 마이크 + AudioContext + analyser 확보. 사용자 제스처(시작 버튼) 안에서 불러야 한다.
 *
 * `preferredSampleRate`가 오면 그 레이트로 컨텍스트를 연다 — 내보낼 PCM의 레이트를
 * 부르는 쪽이 정할 수 있어야 해서다. **요청이 그대로 받아들여진다는 보장은 없다**:
 * 기기·브라우저가 거부하면 예외가 나고, 여기서는 기본 레이트로 되돌아간다. 실제 레이트는
 * 언제나 `ctx.sampleRate`에서 다시 읽어야 한다.
 */
const openMic = async (
  windowSize: number,
  preferredSampleRate?: number,
): Promise<MicAudio | MicFailure> => {
  const Ctor = resolveAudioContextCtor();
  if (!Ctor || !navigator.mediaDevices) {
    return 'unsupported';
  }
  let stream: MediaStream;
  try {
    // 세 처리를 모두 끈다 — AGC/노이즈 억제가 켜져 있으면 튜닝하려는 그 값
    // (입력 레벨·잡음)을 브라우저가 먼저 바꿔 버려 측정이 아니게 된다.
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
  } catch {
    return 'denied';
  }
  const ctx = preferredSampleRate
    ? openContext(Ctor, preferredSampleRate)
    : new Ctor();
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // resume은 best-effort — 실패해도 analyser는 붙여 둔다.
    }
  }
  const analyser = ctx.createAnalyser();
  analyser.fftSize = windowSize;
  // 시간영역 표본을 직접 읽으므로 스무딩은 끈다(analyser의 평활은 주파수영역용이고,
  // 켜 두면 우리가 재려는 raw 파형이 아니게 된다).
  analyser.smoothingTimeConstant = 0;
  const source = ctx.createMediaStreamSource(stream);
  source.connect(analyser);
  return {
    ctx,
    analyser,
    stream,
    source,
    buffer: new Float32Array(analyser.fftSize),
  };
};

const closeMic = (audio: MicAudio | null): void => {
  if (!audio) {
    return;
  }
  for (const track of audio.stream.getTracks()) {
    track.stop();
  }
  if (audio.ctx.state !== 'closed') {
    void audio.ctx.close().catch(() => undefined);
  }
};

/** analyser의 fftSize는 2의 거듭제곱만 받는다 — config 값이 어긋나면 가장 가까운 값으로. */
const legalFftSize = (windowSize: number): number => {
  const clamped = Math.min(32768, Math.max(32, windowSize));
  return 2 ** Math.round(Math.log2(clamped));
};

export interface BrowserSourceOptions {
  /** 이번 `start()`가 어느 소스로 열릴지. 세션 시작 시점에 읽는다. */
  getSource: () => BrowserSource;
  /** 마이크를 못 열었을 때 호출 — 화면이 "왜 안 되는지" 말할 수 있게. */
  onMicFailure: (failure: MicFailure) => void;
  /**
   * 열린 오디오 그래프. 같은 스트림에 PCM 녹음을 물리려는 쪽이 여기서 받아 간다.
   * mock 소스로 열렸을 때는 호출되지 않는다 — 그때는 오디오 그래프 자체가 없다.
   */
  onMicOpen?: (graph: MicGraph) => void;
  /** 마이크가 닫혔을 때. 녹음을 같이 걷어내라는 신호다. */
  onMicClose?: () => void;
  /** 컨텍스트를 이 레이트로 열어 달라는 요청(보장 아님 — `openMic` 주석). */
  preferredSampleRate?: number;
}

export const createBrowserSourceModule = (
  options: BrowserSourceOptions,
): PitchTrackerModule => {
  const listeners: Listeners = new Map();
  // mock은 자기 리스너 체계를 갖는다 — 모든 채널을 우리 레지스트리로 이어 붙인다.
  // 목록을 여기서 복제하면 이벤트 variant가 추가될 때 조용히 빠지는 채널이 생긴다.
  const mock = createMockPitchTrackerModule(undefined, 'melody');
  let active: BrowserSource = 'mock';
  // 핸들러를 루프 밖에 한 번만 만든다(루프 안 클로저가 `active`를 잡으면 lint가 잡는다).
  const relayFromMock = (event: PitchTrackerEvent): void => {
    if (active === 'mock') {
      emit(listeners, event);
    }
  };
  for (const name of PITCH_EVENT_NAMES) {
    mock.addListener(name, relayFromMock);
  }

  let config: PitchTrackerConfig | null = null;
  let audio: MicAudio | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let startedAtMs: number | null = null;
  let frames = 0;

  const stopMicTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const startMicTimer = (): void => {
    stopMicTimer();
    if (!audio || !config) {
      return;
    }
    const current = audio;
    const analysis = () => config?.analysis;
    timer = setInterval(() => {
      const cfg = analysis();
      if (!cfg) {
        return;
      }
      current.analyser.getFloatTimeDomainData(current.buffer);
      const frame = buildMicFrame({
        window: current.buffer,
        sampleRate: current.ctx.sampleRate,
        timestampMs: Date.now(),
        analysis: cfg,
      });
      frames += 1;
      emit(listeners, { type: 'pitch_frame', frame });
      // 레벨 미터는 별도 채널을 구독한다 — 프레임만 보내면 미터가 빈 채로 남는다.
      emit(listeners, {
        type: 'input_level',
        timestampMs: frame.timestampMs,
        rmsDb: frame.rmsDb,
      });
    }, effectiveEventIntervalMs(config));
  };

  const startMic = async (
    next: PitchTrackerConfig,
  ): Promise<PitchTrackerSessionInfo> => {
    const opened = await openMic(
      legalFftSize(next.analysis.windowSize),
      options.preferredSampleRate,
    );
    if (typeof opened === 'string') {
      options.onMicFailure(opened);
      emit(listeners, {
        type: 'error',
        error: {
          code:
            opened === 'denied'
              ? 'microphone_permission_denied'
              : 'microphone_unavailable',
          message:
            opened === 'denied'
              ? '마이크 권한이 거부됐습니다. 브라우저 주소창의 권한 설정에서 허용해 주세요.'
              : '이 브라우저는 마이크 캡처를 지원하지 않습니다.',
          recoverable: opened === 'denied',
        },
      });
      throw new Error(`mic ${opened}`);
    }
    audio = opened;
    options.onMicOpen?.({ ctx: opened.ctx, source: opened.source });
    startedAtMs = Date.now();
    frames = 0;
    const session: PitchTrackerSessionInfo = {
      sessionId: BROWSER_MIC_SESSION_ID,
      startedAtMs,
      captureSampleRate: opened.ctx.sampleRate,
      analysisSampleRate: opened.ctx.sampleRate,
      config: next,
    };
    emit(listeners, { type: 'session_started', session });
    startMicTimer();
    return session;
  };

  return {
    start: (next: PitchTrackerConfig): Promise<PitchTrackerSessionInfo> => {
      config = next;
      active = options.getSource();
      if (active === 'mock') {
        return mock.start(next);
      }
      return startMic(next);
    },
    stop: (): Promise<void> => {
      if (active === 'mock') {
        return mock.stop();
      }
      stopMicTimer();
      options.onMicClose?.();
      closeMic(audio);
      audio = null;
      startedAtMs = null;
      emit(listeners, { type: 'session_stopped', reason: 'user_requested' });
      return Promise.resolve();
    },
    pause: (): Promise<void> => {
      if (active === 'mock') {
        return mock.pause();
      }
      stopMicTimer();
      return Promise.resolve();
    },
    resume: (): Promise<void> => {
      if (active === 'mock') {
        return mock.resume();
      }
      startMicTimer();
      return Promise.resolve();
    },
    updateConfig: (patch: Partial<PitchTrackerConfig>): Promise<void> => {
      config = config ? { ...config, ...patch } : null;
      if (active === 'mock') {
        return mock.updateConfig(patch);
      }
      if (config) {
        emit(listeners, { type: 'config_changed', config });
        // window/hop이 바뀌면 analyser와 방출 주기를 다시 잡아야 실제로 반영된다 —
        // 네이티브가 분석기를 새로 만드는 것과 같은 이유이고, 같은 대가(프레임 한 번 끊김).
        if (audio) {
          const size = legalFftSize(config.analysis.windowSize);
          if (audio.analyser.fftSize !== size) {
            audio.analyser.fftSize = size;
            audio.buffer = new Float32Array(size);
          }
        }
        startMicTimer();
      }
      return Promise.resolve();
    },
    addListener: <N extends PitchTrackerEventName>(
      name: N,
      listener: (event: PitchTrackerEventOf<N>) => void,
    ): Subscription => {
      const set = listeners.get(name) ?? new Set();
      // 레지스트리는 이름으로 나뉘어 있고 `emit`은 `event.type`으로만 꺼내므로, 이 채널에
      // 들어오는 이벤트는 항상 그 이름의 variant다 — 캐스트가 그 사실을 좁혀 준다.
      const erased = listener as (event: PitchTrackerEvent) => void;
      set.add(erased);
      listeners.set(name, set);
      return { remove: () => set.delete(erased) };
    },
    getDiagnosticsSnapshot: (): Promise<DiagnosticsSnapshot> => {
      if (active === 'mock') {
        return mock.getDiagnosticsSnapshot();
      }
      // 마이크 경로의 진단은 **아는 것만** 낸다. 네이티브 카운터(ring overrun·callback
      // budget·thermal)는 브라우저에 존재하지 않으므로 낙관값을 채우지 않는다 — mock이
      // 무조건 "running"을 답해서 진단 패널이 죽은 걸 못 잡았던 게 그 실패다.
      // `detector.algorithm`은 'unknown'이다: 이 경로는 ACF이고 그 union에 ACF가 없으니
      // 아는 이름을 빌려 쓰면 거짓이 된다.
      return mock.getDiagnosticsSnapshot().then((base) => ({
        ...base,
        audioInput: {
          ...base.audioInput,
          backend: 'unknown' as const,
          permissionState: (audio ? 'granted' : 'unknown') as
            | 'granted'
            | 'unknown',
          actualSampleRate: audio?.ctx.sampleRate ?? null,
        },
        detector: {
          ...base.detector,
          algorithm: 'unknown' as const,
          active: timer !== null,
        },
        eventDispatcher: {
          ...base.eventDispatcher,
          emittedPitchFrameCount: frames,
        },
      }));
    },
  };
};
